<template>
  <ButtonBase
    class="whitespace-no-wrap"
    :class="rootClassName"
    :component="is"
    :disabled="disabled || loading"
    v-on="$listeners"
    v-bind="$attrs"
  >
    <span v-if="$slots['start-icon']" :class="startIconClassName">
      <template v-if="loading">
        <Icon class="fa-spin" name="spinner" />
      </template>
      <template v-else>
        <slot name="start-icon" />
      </template>
    </span>
    <span
      v-if="!$slots['start-icon'] && !$slots['end-icon'] && loading"
      :class="startIconClassName"
    >
      <Icon class="fa-spin" name="spinner" />
    </span>

    <slot />

    <span v-if="$slots['end-icon']" :class="endIconClassName">
      <template v-if="loading">
        <Icon class="fa-spin" name="spinner" />
      </template>
      <template v-else>
        <slot name="end-icon" />
      </template>
    </span>
  </ButtonBase>
</template>

<script>
import ButtonBase from '../ButtonBase';
import Icon from '../Icon';

export default {
  name: 'Button',

  components: {ButtonBase, Icon},

  props: {
    loading: Boolean,

    fullwidth: Boolean,

    disabled: Boolean,

    variant: {
      type: String,
      default: 'basic',
      validator: (v) => ['basic', 'outlined', 'text'].includes(v),
    },

    color: {
      type: String,
      default: 'default',
      validator: (v) =>
        ['default', 'primary', 'secondary', 'danger'].includes(v),
    },

    size: {
      type: String,
      default: 'medium',
      validator: (v) => ['small', 'medium', 'large'].includes(v),
    },

    component: {
      type: [String, Object],
    },
  },

  computed: {
    is() {
      if (this.component) {
        return this.component;
      }
      return 'button';
    },

    rootClassName() {
      return [
        this.$style.root,
        this.$style[this.variant],
        this.$style[`size-${this.size}`],
        this.$style[`variant-${this.variant}-${this.color}`],
        {
          'w-full': this.fullwidth,
          [this.$style.disabled]: this.disabled,
          [this.$style.loading]: this.loading,
        },
      ];
    },

    startIconClassName() {
      return {
        small: 'mr-2',
        medium: 'mr-2',
        large: 'mr-2',
      }[this.size];
    },

    endIconClassName() {
      return {
        small: 'ml-2',
        medium: 'ml-4',
        large: 'ml-6',
      }[this.size];
    },

    loadingClassName() {
      return [this.$style.loading];
    },
  },
};
</script>

<style module>
.loading {
  @apply opacity-75;
}

.root {
  @apply inline-block
		font-medium
    rounded;
}

.root:focus {
  @apply outline-none;
}

.size-small {
  @apply py-1
    px-4
    text-sm;
}

.size-medium {
  @apply py-2
    px-4
    text-base;
}

.size-large {
  @apply py-4
    px-8
    text-base;
}

.variant-basic-default {
  @apply border
    border-dull
    text-dull-dark
    bg-white;
}

.variant-basic-default:hover {
  @apply bg-dull-light;
}

.variant-basic-primary {
  @apply text-white
    bg-primary;
}

.variant-basic-primary:hover {
  @apply bg-primary-dark;
}

.variant-basic-secondary {
  @apply text-white
    bg-secondary;
}

.variant-basic-secondary:hover {
  @apply bg-secondary-dark;
}

.outlined {
  @apply border
    border-solid;
}

.variant-outlined-default {
  @apply text-secondary
    border-dull;
}

.variant-outlined-default:hover {
  @apply bg-dull-light
    border-dull;
}

.variant-outlined-primary {
  @apply text-primary
    border-primary;
}

.variant-outlined-primary:hover {
  @apply bg-dull-light
    border-primary;
}

.variant-outlined-secondary {
  @apply text-secondary
    border-secondary;
}

.variant-outlined-secondary:hover {
  @apply bg-secondary-light
    border-secondary;
}

.variant-basic-danger {
  @apply text-white
  bg-danger;
}

.variant-basic-danger:hover {
  @apply bg-danger-dark;
}

.variant-outlined-danger {
  @apply text-danger
  border-danger;
}

.variant-outlined-danger:hover {
  @apply text-danger-dark
  border-danger;
}

.text {
  @apply bg-transparent
		border
		border-solid
		border-transparent;
}

.variant-text-default {
  @apply text-dull-dark;
}

.variant-text-default:hover {
  @apply bg-dull-light
		border-dull-dark;
}

.variant-text-primary {
  @apply text-primary;
}

.variant-text-primary:hover {
  @apply bg-dull-light;
}

.variant-text-secondary {
  @apply text-secondary;
}

.variant-text-secondary:hover {
  @apply bg-secondary-light
		border-secondary;
}

.variant-danger-primary {
  @apply text-danger;
}

.variant-danger-primary:hover {
  @apply bg-dull-light;
}

.disabled {
  @apply cursor-not-allowed
    opacity-75;
}

.disabled:hover {
  @apply cursor-not-allowed;
}

.loading {
  display: block;
  min-width: 100px;
  @apply cursor-not-allowed;
}
</style>
